import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import {
  Company,
  CompanyUser,
  CompanyBranding,
  DeleteConfirmation,
  CreateCompanyPayload,
  CreateCompanyUserPayload,
  CompanyLock,
  CompanyGroup,
  LockAssignment,
  GroupAssignment,
  CompanyUsersImportResult,
} from '../types';
import {
  Building2,
  Plus,
  Trash2,
  Users,
  ChevronLeft,
  Edit3,
  Key,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  X,
  Shield,
  UserPlus,
  Settings,
  Palette,
  FolderOpen,
  ToggleLeft,
  ToggleRight,
  Filter,
  Upload,
  Download,
  Info,
} from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';

const CSV_IMPORT_CHUNK_SIZE = 1000;

// ─── Sub-views ───────────────────────────────────────────────
type View =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'detail'; company: Company }
  | { kind: 'edit'; company: Company }
  | { kind: 'addUser'; company: Company }
  | { kind: 'manageLocks'; company: Company; user: CompanyUser };

type CsvImportChunk = {
  file: File;
  startRowOffset: number;
};

const splitCsvRecords = (text: string): string[] => {
  const records: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += char + text[i + 1];
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
    }

    if (char === '\n' && !inQuotes) {
      records.push(current.endsWith('\r') ? current.slice(0, -1) : current);
      current = '';
      continue;
    }

    current += char;
  }

  if (current.length > 0) {
    records.push(current.endsWith('\r') ? current.slice(0, -1) : current);
  }

  return records;
};

const buildCsvImportChunks = async (file: File, chunkSize: number): Promise<CsvImportChunk[]> => {
  const records = splitCsvRecords(await file.text());
  if (records.length === 0) {
    throw new Error('CSV header is required');
  }

  const header = records[0];
  const rows = records.slice(1).filter((row) => row.trim().length > 0);
  const baseName = file.name.replace(/\.csv$/i, '');

  if (rows.length === 0) {
    return [{
      file: new File([`${header}\n`], `${baseName}.part-1.csv`, { type: 'text/csv' }),
      startRowOffset: 0,
    }];
  }

  const chunks: CsvImportChunk[] = [];
  for (let start = 0; start < rows.length; start += chunkSize) {
    const partRows = rows.slice(start, start + chunkSize);
    chunks.push({
      file: new File([`${header}\n${partRows.join('\n')}\n`], `${baseName}.part-${chunks.length + 1}.csv`, { type: 'text/csv' }),
      startRowOffset: start,
    });
  }

  return chunks;
};

const escapeCsvValue = (value: string | number | null | undefined): string => {
  const text = value == null ? '' : String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const downloadImportErrors = (result: CompanyUsersImportResult, companyCode: string) => {
  const header = ['row', 'username', 'error'];
  const rows = result.errors.map((err) => [
    err.row,
    err.username || '',
    err.error,
  ]);
  const csv = [
    header.join(','),
    ...rows.map((row) => row.map(escapeCsvValue).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const safeCode = companyCode.replace(/[^a-z0-9_-]/gi, '_');
  link.href = url;
  link.download = `${safeCode}_import_errors.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const CompanyManagement: React.FC = () => {
  // ── state ──────────────────────────────────────────────────
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [userLimit, setUserLimit] = useState(50);
  const [userOffset, setUserOffset] = useState(0);
  const [userTotal, setUserTotal] = useState(0);
  const [userHasMore, setUserHasMore] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // create company form
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newMaxUsers, setNewMaxUsers] = useState<string>('');
  const [newBranding, setNewBranding] = useState<CompanyBranding>({});
  const [newLoginMode, setNewLoginMode] = useState<'password' | 'code_only'>('password');

  // edit company form
  const [editName, setEditName] = useState('');
  const [editMaxUsers, setEditMaxUsers] = useState<string>('');
  const [editBranding, setEditBranding] = useState<CompanyBranding>({});
  const [editIsActive, setEditIsActive] = useState(true);

  // create user form
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newRole, setNewRole] = useState<'user' | 'admin'>('user');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // import users
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importDryRun, setImportDryRun] = useState(false);
  const [importUpdateExisting, setImportUpdateExisting] = useState(false);
  const [importResult, setImportResult] = useState<CompanyUsersImportResult | null>(null);
  const [isImportingUsers, setIsImportingUsers] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);

  // reset password
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  // lock assignment
  const [availableLocks, setAvailableLocks] = useState<CompanyLock[]>([]);
  const [availableGroups, setAvailableGroups] = useState<CompanyGroup[]>([]);
  const [assignedUnits, setAssignedUnits] = useState<LockAssignment[]>([]);
  const [assignedGroups, setAssignedGroups] = useState<GroupAssignment[]>([]);
  const [locksLoading, setLocksLoading] = useState(false);

  const [confirmation, setConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });
  const [actionLoading, setActionLoading] = useState(false);

  // filters
  const [companyFilter, setCompanyFilter] = useState('');
  const [lockAssignFilter, setLockAssignFilter] = useState('');

  // settings checkboxes for available items (before assigning)
  const [settingsChecked, setSettingsChecked] = useState<Record<string, boolean>>({});

  // ── view persistence (survive browser refresh) ──────────────
  const viewRestored = useRef(false);
  const companyUsersRequestId = useRef(0);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  // ── loaders ────────────────────────────────────────────────
  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getMyCompanies();
      if (res.success && res.data) setCompanies(res.data);
    } catch (e) {
      console.error('Error loading companies', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompanyUsers = useCallback(async (
    companyId: string,
    options: { search: string; limit: number; offset: number }
  ) => {
    const requestId = companyUsersRequestId.current + 1;
    companyUsersRequestId.current = requestId;
    try {
      setIsLoadingUsers(true);
      const res = await apiService.getCompanyUsers(companyId, options);
      if (requestId !== companyUsersRequestId.current) return;
      if (res.success && res.data) {
        setCompanyUsers(res.data.users);
        setUserTotal(res.data.total);
        setUserLimit(res.data.limit);
        setUserOffset(res.data.offset);
        setUserHasMore(res.data.has_more);
      }
    } catch (e) {
      if (requestId !== companyUsersRequestId.current) return;
      console.error('Error loading company users', e);
    } finally {
      if (requestId === companyUsersRequestId.current) {
        setIsLoadingUsers(false);
      }
    }
  }, []);

  const refreshCurrentUsersPage = useCallback(async (companyId: string) => {
    await loadCompanyUsers(companyId, {
      search: debouncedUserSearch,
      limit: userLimit,
      offset: userOffset,
    });
  }, [debouncedUserSearch, loadCompanyUsers, userLimit, userOffset]);

  const loadLockAssignments = useCallback(async (companyId: string, userId: string) => {
    try {
      setLocksLoading(true);
      const [locksRes, groupsRes, assignmentsRes] = await Promise.all([
        apiService.getCompanyLocks(companyId),
        apiService.getCompanyGroups(companyId),
        apiService.getUserLockAssignments(companyId, userId),
      ]);
      if (locksRes.success && locksRes.data) setAvailableLocks(locksRes.data);
      if (groupsRes.success && groupsRes.data) setAvailableGroups(groupsRes.data);
      if (assignmentsRes.success && assignmentsRes.data) {
        setAssignedUnits(assignmentsRes.data.shared_units);
        setAssignedGroups(assignmentsRes.data.shared_groups);
      }
    } catch (e) {
      console.error('Error loading lock assignments', e);
    } finally {
      setLocksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Save view to sessionStorage on every navigation
  useEffect(() => {
    if (view.kind === 'list' || view.kind === 'create') {
      // Only clear once we've already restored — otherwise on refresh the initial
      // 'list' view would wipe the saved state before we get a chance to read it.
      if (viewRestored.current) sessionStorage.removeItem('lw_view');
      return;
    } else if (view.kind === 'detail') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'detail', companyId: view.company.id }));
    } else if (view.kind === 'manageLocks') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'manageLocks', companyId: view.company.id, userId: view.user.id }));
    } else if (view.kind === 'edit') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'detail', companyId: view.company.id }));
    } else if (view.kind === 'addUser') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'detail', companyId: view.company.id }));
    }
  }, [view]);

  // Restore view after companies load (once per session)
  useEffect(() => {
    if (companies.length === 0 || viewRestored.current) return;
    viewRestored.current = true;
    const saved = sessionStorage.getItem('lw_view');
    if (!saved) return;
    try {
      const state = JSON.parse(saved);
      const company = companies.find((c) => c.id === state.companyId);
      if (!company) return;
      if (state.kind === 'detail') {
        setView({ kind: 'detail', company });
      } else if (state.kind === 'manageLocks') {
        setView({ kind: 'detail', company });
      }
    } catch {
      sessionStorage.removeItem('lw_view');
    }
  }, [companies]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedUserSearch(userSearch.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    if (view.kind === 'detail') {
      loadCompanyUsers(view.company.id, {
        search: debouncedUserSearch,
        limit: userLimit,
        offset: userOffset,
      });
    }
    if (view.kind === 'manageLocks') loadLockAssignments(view.company.id, view.user.id);
  }, [view, debouncedUserSearch, loadCompanyUsers, loadLockAssignments, userLimit, userOffset]);

  // ── helpers ────────────────────────────────────────────────
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const showConfirmation = (title: string, message: string, onConfirm: () => void) => {
    setConfirmation({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmation((p) => ({ ...p, isOpen: false }));
      },
      onCancel: () => setConfirmation((p) => ({ ...p, isOpen: false })),
    });
  };

  // ── actions ────────────────────────────────────────────────
  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (!newCode.trim() || !newName.trim()) {
      setError('Code and name are required');
      return;
    }
    if (newCode.trim().length < 3) {
      setError('Code must be at least 3 characters');
      return;
    }
    if (!/^[A-Za-z0-9-]+$/.test(newCode.trim())) {
      setError('Code can only contain letters, numbers and hyphens');
      return;
    }
    setActionLoading(true);
    const payload: CreateCompanyPayload = {
      code: newCode.trim(),
      name: newName.trim(),
      branding: Object.keys(newBranding).length > 0 ? newBranding : undefined,
      max_users: newMaxUsers ? parseInt(newMaxUsers, 10) : null,
      login_mode: newLoginMode,
    };
    const res = await apiService.createCompany(payload);
    setActionLoading(false);
    if (res.success) {
      setSuccess('Company created successfully');
      setNewCode('');
      setNewName('');
      setNewMaxUsers('');
      setNewBranding({});
      setNewLoginMode('password');
      await loadCompanies();
      setTimeout(() => setView({ kind: 'list' }), 800);
    } else {
      setError(res.message || 'Failed to create company');
    }
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (view.kind !== 'edit') return;
    setActionLoading(true);
    const res = await apiService.updateCompany(view.company.id, {
      name: editName.trim() || undefined,
      branding: Object.keys(editBranding).length > 0 ? editBranding : undefined,
      max_users: editMaxUsers ? parseInt(editMaxUsers, 10) : undefined,
      is_active: editIsActive,
    });
    setActionLoading(false);
    if (res.success) {
      setSuccess('Company updated');
      await loadCompanies();
      setTimeout(() => setView({ kind: 'list' }), 800);
    } else {
      setError(res.message || 'Failed to update company');
    }
  };

  const handleDeleteCompany = (company: Company) => {
    showConfirmation(
      'Delete Company',
      `Are you sure you want to delete "${company.name}" (${company.code})? This will also deactivate all its users. This action cannot be undone.`,
      async () => {
        const res = await apiService.deleteCompany(company.id);
        if (res.success) {
          await loadCompanies();
          setView({ kind: 'list' });
        }
      },
    );
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (view.kind !== 'addUser') return;
    const isPasswordMode = (view.company.login_mode ?? 'password') === 'password';
    if (!newUsername.trim() || (isPasswordMode && !newPassword)) {
      setError(isPasswordMode ? 'Username and password are required' : 'Access code is required');
      return;
    }
    setActionLoading(true);
    const payload: CreateCompanyUserPayload = {
      username: newUsername.trim(),
      password: isPasswordMode ? newPassword : undefined,
      first_name: newFirstName.trim() || undefined,
      last_name: newLastName.trim() || undefined,
      role: newRole,
    };
    const res = await apiService.createCompanyUser(view.company.id, payload);
    setActionLoading(false);
    if (res.success) {
      setSuccess(`User "${newUsername}" created`);
      setNewUsername('');
      setNewPassword('');
      setNewFirstName('');
      setNewLastName('');
      setNewRole('user');
      setTimeout(() => {
        setView({ kind: 'detail', company: view.company });
      }, 800);
    } else {
      setError(res.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = (companyId: string, user: CompanyUser) => {
    showConfirmation(
      'Delete User',
      `Are you sure you want to delete user "${user.company_username}" (${user.full_name})? This action cannot be undone.`,
      async () => {
        const res = await apiService.deleteCompanyUser(companyId, user.id);
        if (res.success) refreshCurrentUsersPage(companyId);
      },
    );
  };

  const handleResetPassword = async (companyId: string, e?: React.FormEvent) => {
    e?.preventDefault();
    clearMessages();
    const userId = resetUserId;
    const newPassword = resetPassword.trim();

    if (!userId) {
      setError('Choose a user to reset first');
      return;
    }
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }

    setActionLoading(true);
    const res = await apiService.resetCompanyUserPassword(companyId, userId, newPassword);
    setActionLoading(false);
    if (res.success) {
      setSuccess('Password reset successfully');
      setResetUserId(null);
      setResetPassword('');
      await refreshCurrentUsersPage(companyId);
    } else {
      setError(res.message || 'Failed to reset password');
    }
  };

  const handleImportUsers = async (companyId: string) => {
    clearMessages();
    setImportResult(null);
    if (!importFile) {
      setError('Choose a CSV file first');
      return;
    }

    const aggregate: CompanyUsersImportResult = {
      dry_run: importDryRun,
      created: 0,
      updated: 0,
      skipped: 0,
      errors: [],
      total_rows: 0,
    };

    setIsImportingUsers(true);
    try {
      const chunks = await buildCsvImportChunks(importFile, CSV_IMPORT_CHUNK_SIZE);
      setImportProgress({ current: 0, total: chunks.length });

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        setImportProgress({ current: i + 1, total: chunks.length });
        const res = await apiService.importCompanyUsers(companyId, chunk.file, {
          dryRun: importDryRun,
          updateExisting: importUpdateExisting,
        });

        if (!res.success || !res.data) {
          throw new Error(`Batch ${i + 1}/${chunks.length}: ${res.message || 'Failed to import users'}`);
        }

        aggregate.created += res.data.created;
        aggregate.updated += res.data.updated;
        aggregate.skipped += res.data.skipped;
        aggregate.total_rows += res.data.total_rows;
        aggregate.errors.push(...res.data.errors.map((err) => ({
          ...err,
          row: err.row > 1 ? err.row + chunk.startRowOffset : err.row,
        })));
        setImportResult({ ...aggregate, errors: [...aggregate.errors] });
      }

      setSuccess(importDryRun ? 'CSV validation completed' : 'CSV import completed');
      if (!importDryRun) {
        await refreshCurrentUsersPage(companyId);
        await loadCompanies();
      }
    } catch (e: any) {
      setError(e.message || 'Failed to import users');
      if (aggregate.total_rows > 0 || aggregate.errors.length > 0) {
        setImportResult({ ...aggregate, errors: [...aggregate.errors] });
      }
    } finally {
      setIsImportingUsers(false);
      setImportProgress(null);
    }
  };

  // ── lock assignment actions ────────────────────────────────
  const handleAssignLock = async (companyId: string, userId: string, unitId: string, allowSettings: boolean) => {
    clearMessages();
    setActionLoading(true);
    const res = await apiService.assignLockToUser(companyId, userId, {
      unit_id: unitId,
      allow_settings_changes: allowSettings,
    });
    setActionLoading(false);
    if (res.success) {
      setSuccess(res.data?.message || 'Lock assigned');
      await loadLockAssignments(companyId, userId);
    } else {
      setError(res.message || 'Failed to assign lock');
    }
  };

  const handleAssignGroup = async (companyId: string, userId: string, groupId: string, allowSettings: boolean) => {
    clearMessages();
    setActionLoading(true);
    const res = await apiService.assignLockToUser(companyId, userId, {
      group_id: groupId,
      allow_settings_changes: allowSettings,
    });
    setActionLoading(false);
    if (res.success) {
      setSuccess(res.data?.message || 'Group assigned');
      await loadLockAssignments(companyId, userId);
    } else {
      setError(res.message || 'Failed to assign group');
    }
  };

  const handleUnassignLock = async (companyId: string, userId: string, assignmentId: string, name: string) => {
    showConfirmation(
      'Remove Lock',
      `Remove lock "${name}" from this user? They will lose access on next sync.`,
      async () => {
        const res = await apiService.unassignLockFromUser(companyId, userId, assignmentId, 'unit');
        if (res.success) {
          setSuccess('Lock removed');
          await loadLockAssignments(companyId, userId);
        } else {
          setError(res.message || 'Failed to remove lock');
        }
      },
    );
  };

  const handleUnassignGroup = async (companyId: string, userId: string, assignmentId: string, name: string) => {
    showConfirmation(
      'Remove Group',
      `Remove group "${name}" from this user? They will lose access to all locks in the group.`,
      async () => {
        const res = await apiService.unassignLockFromUser(companyId, userId, assignmentId, 'group');
        if (res.success) {
          setSuccess('Group removed');
          await loadLockAssignments(companyId, userId);
        } else {
          setError(res.message || 'Failed to remove group');
        }
      },
    );
  };

  const handleToggleSettings = async (
    companyId: string, userId: string, assignmentId: string,
    current: boolean, type: 'unit' | 'group'
  ) => {
    clearMessages();
    const res = await apiService.updateLockAssignment(companyId, userId, assignmentId, !current, type);
    if (res.success) {
      await loadLockAssignments(companyId, userId);
    } else {
      setError(res.message || 'Failed to update settings permission');
    }
  };

  // ── Open edit view with pre-populated data ─────────────────
  const openEditView = (company: Company) => {
    setEditName(company.name);
    setEditMaxUsers(company.max_users != null ? String(company.max_users) : '');
    setEditBranding(company.branding || {});
    setEditIsActive(company.is_active);
    clearMessages();
    setView({ kind: 'edit', company });
  };

  // ── Branding field helper ──────────────────────────────────
  const BrandingFields: React.FC<{
    branding: CompanyBranding;
    onChange: (b: CompanyBranding) => void;
  }> = ({ branding, onChange }) => (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-slate-600 flex items-center">
        <Palette className="h-4 w-4 mr-1.5 text-violet-500" />
        Branding (optional)
      </label>
      {[
        { key: 'logo_url' as const, label: 'Logo URL', ph: 'https://…/logo.png' },
        { key: 'primary_color' as const, label: 'Primary Color', ph: '#4F46E5' },
        { key: 'secondary_color' as const, label: 'Secondary Color', ph: '#7C3AED' },
        { key: 'accent_color' as const, label: 'Accent Color', ph: '#10B981' },
        { key: 'welcome_text' as const, label: 'Welcome Text', ph: 'Welcome to Company' },
        { key: 'background_image_url' as const, label: 'Background Image URL', ph: 'https://…/bg.jpg' },
      ].map(({ key, label, ph }) => (
        <div key={key}>
          <label className="block text-xs text-slate-500 mb-1">{label}</label>
          <input
            type="text"
            placeholder={ph}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
            value={branding[key] || ''}
            onChange={(e) =>
              onChange({
                ...branding,
                [key]: e.target.value || undefined,
              })
            }
          />
        </div>
      ))}
    </div>
  );

  // ── feedback banner ────────────────────────────────────────
  const FeedbackBanner: React.FC = () => (
    <>
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 mb-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-red-100 rounded">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 mb-4">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto p-1 hover:bg-emerald-100 rounded">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </>
  );

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-indigo-600" />
        <p className="text-sm font-medium text-slate-400">Loading companies...</p>
      </div>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────
  if (view.kind === 'list') {
    return (
      <>
        <ConfirmDialog confirmation={confirmation} />
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-amber-500" />
              My Companies ({companies.length})
            </h2>
            <button
              onClick={() => {
                clearMessages();
                setView({ kind: 'create' });
              }}
              className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all shadow-sm hover:shadow"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              New Company
            </button>
          </div>

          {/* Company cards */}
          {companies.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No companies yet</h3>
              <p className="text-sm text-slate-500 mb-6">
                Create your first company to start adding users without personal emails.
              </p>
              <button
                onClick={() => {
                  clearMessages();
                  setView({ kind: 'create' });
                }}
                className="inline-flex items-center px-5 py-2.5 text-sm font-semibold bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all shadow-sm"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Company
              </button>
            </div>
          ) : (
            <>
            {/* Company filter */}
            <div className="relative max-w-md">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Filter companies..."
                className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all shadow-sm"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {companies
                .filter(company => {
                  if (!companyFilter) return true;
                  const q = companyFilter.toLowerCase();
                  return company.name.toLowerCase().includes(q) || company.code.toLowerCase().includes(q);
                })
                .map((company) => (
                <div
                  key={company.id}
                  className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all"
                >
                  <div className="h-1 bg-amber-500" />
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{company.name}</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 mt-1">
                          {company.code}
                        </span>
                      </div>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          company.is_active
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                      >
                        {company.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-5">
                      <span className="flex items-center">
                        <Users className="h-4 w-4 mr-1 text-slate-400" />
                        {company.user_count ?? 0} users
                      </span>
                      <span>
                        Max: {company.max_users != null ? company.max_users : '∞'}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        (company.login_mode ?? 'password') === 'code_only'
                          ? 'bg-violet-50 text-violet-700 border border-violet-200'
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}>
                        {(company.login_mode ?? 'password') === 'code_only' ? <Shield className="h-3 w-3" /> : <Key className="h-3 w-3" />}
                        {(company.login_mode ?? 'password') === 'code_only' ? 'Code Only' : 'Password'}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          clearMessages();
                          setUserSearch('');
                          setDebouncedUserSearch('');
                          setUserOffset(0);
                          setImportResult(null);
                          setImportFile(null);
                          setView({ kind: 'detail', company });
                        }}
                        className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        <Users className="h-4 w-4 mr-1.5" />
                        Users
                      </button>
                      <button
                        onClick={() => openEditView(company)}
                        className="flex items-center px-3 py-2 text-sm font-medium bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCompany(company)}
                        className="flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </>
    );
  }

  // ── CREATE COMPANY VIEW ────────────────────────────────────
  if (view.kind === 'create') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => setView({ kind: 'list' })}
          className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to companies
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="h-1 bg-amber-500" />
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center">
              <Plus className="h-5 w-5 mr-2 text-amber-500" />
              Create New Company
            </h2>
          </div>
          <form onSubmit={handleCreateCompany} className="p-6 space-y-5">
            <FeedbackBanner />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                  Company Code *
                </label>
                <input
                  type="text"
                  placeholder="ACME2026"
                  maxLength={10}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm uppercase focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">3-10 chars, letters, numbers, hyphens (no spaces)</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                  Company Name *
                </label>
                <input
                  type="text"
                  placeholder="Acme Corporation"
                  maxLength={20}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">{newName.length}/20 characters</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                Login Mode *
              </label>
              <div className="flex gap-3">
                {(['password', 'code_only'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setNewLoginMode(mode)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${
                      newLoginMode === mode
                        ? 'bg-amber-50 text-amber-700 border-amber-400 ring-2 ring-amber-500/20'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {mode === 'password' ? (
                      <>
                        <Key className="h-4 w-4" />
                        Username + Password
                      </>
                    ) : (
                      <>
                        <Shield className="h-4 w-4" />
                        Code Only
                      </>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                {newLoginMode === 'password'
                  ? 'Users authenticate with a username and password'
                  : 'Users authenticate with a single access code — no password needed'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                Max Users <span className="font-normal text-slate-400">(leave empty for unlimited)</span>
              </label>
              <input
                type="number"
                min={1}
                placeholder="Unlimited"
                className="w-full sm:w-48 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                value={newMaxUsers}
                onChange={(e) => setNewMaxUsers(e.target.value)}
              />
            </div>

            <BrandingFields branding={newBranding} onChange={setNewBranding} />

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setView({ kind: 'list' })}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-xl hover:bg-amber-700 disabled:opacity-50 transition-all shadow-sm"
              >
                {actionLoading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── EDIT COMPANY VIEW ──────────────────────────────────────
  if (view.kind === 'edit') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => setView({ kind: 'list' })}
          className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to companies
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="h-1 bg-violet-500" />
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center">
              <Settings className="h-5 w-5 mr-2 text-violet-500" />
              Edit Company — {view.company.code}
            </h2>
          </div>
          <form onSubmit={handleUpdateCompany} className="p-6 space-y-5">
            <FeedbackBanner />

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Company Name</label>
              <input
                type="text"
                maxLength={20}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">{editName.length}/20 characters</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Max Users</label>
              <input
                type="number"
                min={1}
                placeholder="Unlimited"
                className="w-full sm:w-48 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                value={editMaxUsers}
                onChange={(e) => setEditMaxUsers(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-slate-600">Active</label>
              <button
                type="button"
                onClick={() => setEditIsActive(!editIsActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editIsActive ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                    editIsActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <BrandingFields branding={editBranding} onChange={setEditBranding} />

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setView({ kind: 'list' })}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 disabled:opacity-50 shadow-sm"
              >
                {actionLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── COMPANY DETAIL (USERS LIST) ────────────────────────────
  if (view.kind === 'detail') {
    const { company } = view;
    const isPasswordMode = (company.login_mode ?? 'password') === 'password';
    const displayedFrom = userTotal === 0 ? 0 : userOffset + 1;
    const displayedTo = userOffset + companyUsers.length;
    return (
      <>
        <ConfirmDialog confirmation={confirmation} />
        <div className="space-y-6">
          <button
            onClick={() => setView({ kind: 'list' })}
            className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to companies
          </button>

          {/* Company header card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="h-1 bg-amber-500" />
            <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{company.name}</h2>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    {company.code}
                  </span>
                  <span className="text-sm text-slate-500">
                    {userTotal} / {company.max_users ?? '∞'} users
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  clearMessages();
                  setView({ kind: 'addUser', company });
                }}
                className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-sm"
              >
                <UserPlus className="h-4 w-4 mr-1.5" />
                Add User
              </button>
            </div>
          </div>

          <FeedbackBanner />

          {/* Import CSV */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-base font-semibold text-slate-800 flex items-center">
                <Upload className="h-4 w-4 mr-2 text-indigo-500" />
                Import CSV
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-slate-600 mb-1.5">CSV file</label>
                  <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      setImportFile(e.target.files?.[0] || null);
                      setImportResult(null);
                      setImportProgress(null);
                    }}
                    className="hidden"
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <button
                      type="button"
                      onClick={() => importFileInputRef.current?.click()}
                      className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-xl hover:bg-indigo-100"
                    >
                      Choose file
                    </button>
                    <span className="text-sm text-slate-500 truncate">
                      {importFile ? importFile.name : 'No file selected'}
                    </span>
                  </div>
                </div>
                <label
                  className="flex items-center gap-2 text-sm text-slate-600"
                  title="Checks the CSV and shows what would happen, without creating or updating users."
                >
                  <input
                    type="checkbox"
                    checked={importDryRun}
                    onChange={(e) => setImportDryRun(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                  />
                  Check only
                  <Info className="h-3.5 w-3.5 text-slate-400" />
                </label>
                <label
                  className="flex items-center gap-2 text-sm text-slate-600"
                  title="If a username already exists, update its name, role and password instead of skipping it."
                >
                  <input
                    type="checkbox"
                    checked={importUpdateExisting}
                    onChange={(e) => setImportUpdateExisting(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                  />
                  Update existing users
                  <Info className="h-3.5 w-3.5 text-slate-400" />
                </label>
                <button
                  onClick={() => handleImportUsers(company.id)}
                  disabled={isImportingUsers || !importFile}
                  title={importDryRun ? 'Check the CSV without saving changes' : 'Import users from the selected CSV'}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
                >
                  <Upload className="h-4 w-4 mr-1.5" />
                  {isImportingUsers ? 'Processing...' : importDryRun ? 'Check CSV' : 'Import'}
                </button>
              </div>

              {importProgress && (
                <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
                  Processing batch {importProgress.current} of {importProgress.total}
                </div>
              )}

              {importResult && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="flex flex-wrap gap-4">
                    <span>Total rows: <strong>{importResult.total_rows}</strong></span>
                    <span>Created: <strong>{importResult.created}</strong></span>
                    <span>Updated: <strong>{importResult.updated}</strong></span>
                    <span>Skipped: <strong>{importResult.skipped}</strong></span>
                    <span>Mode: <strong>{importResult.dry_run ? 'Validation only' : 'Import'}</strong></span>
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto border-t border-slate-200 pt-3">
                      <button
                        type="button"
                        onClick={() => downloadImportErrors(importResult, company.code)}
                        title="Download all import errors as a CSV file"
                        className="mb-2 inline-flex items-center px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download errors
                      </button>
                      {importResult.errors.slice(0, 50).map((err) => (
                        <div key={`${err.row}-${err.username}-${err.error}`} className="text-xs text-red-700">
                          Row {err.row} ({err.username || 'empty'}): {err.error}
                        </div>
                      ))}
                      {importResult.errors.length > 50 && (
                        <div className="text-xs text-slate-500 mt-1">
                          Showing first 50 of {importResult.errors.length} errors
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Reset password modal inline - only for password mode companies */}
          {resetUserId && isPasswordMode && (
            <form
              onSubmit={(e) => handleResetPassword(company.id, e)}
              className="bg-white rounded-2xl shadow-sm border border-indigo-200 p-5"
            >
              <h3 className="text-sm font-semibold text-slate-800 flex items-center mb-3">
                <Key className="h-4 w-4 mr-1.5 text-indigo-500" />
                Reset Password for {companyUsers.find((u) => u.id === resetUserId)?.company_username}
              </h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1 relative">
                  <input
                    type={showResetPassword ? 'text' : 'password'}
                    placeholder="New password (min 8 chars, 1 uppercase, 1 number)"
                    className="w-full px-4 py-2.5 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={actionLoading || !resetPassword}
                  className="px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
                >
                  {actionLoading ? 'Resetting...' : 'Reset'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setResetUserId(null);
                    setResetPassword('');
                  }}
                  className="px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Users table */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-indigo-500" />
                  Company Users
                </h3>
                <span className="text-sm text-slate-500">
                  Showing {displayedFrom}-{displayedTo} of {userTotal}
                </span>
              </div>
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Filter className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search users..."
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setUserOffset(0);
                    }}
                  />
                </div>
                <select
                  value={userLimit}
                  onChange={(e) => {
                    setUserLimit(Number(e.target.value));
                    setUserOffset(0);
                  }}
                  className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                >
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                  <option value={200}>200 / page</option>
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => setUserOffset(Math.max(0, userOffset - userLimit))}
                    disabled={isLoadingUsers || userOffset === 0}
                    className="px-3 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setUserOffset(userOffset + userLimit)}
                    disabled={isLoadingUsers || !userHasMore}
                    className="px-3 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Full Name
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {isLoadingUsers && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500 text-sm">
                        Loading users...
                      </td>
                    </tr>
                  )}
                  {!isLoadingUsers && companyUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3.5 px-6 text-sm font-medium text-slate-900">
                        {u.company_username}
                      </td>
                      <td className="py-3.5 px-6 text-sm text-slate-600">{u.full_name}</td>
                      <td className="py-3.5 px-6 text-sm">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            u.company_role === 'admin'
                              ? 'bg-violet-50 text-violet-700 border border-violet-200'
                              : 'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          <Shield className="h-3 w-3" />
                          {u.company_role}
                        </span>
                      </td>
                      <td className="py-3.5 px-6 text-sm">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => {
                              clearMessages();
                              setView({ kind: 'manageLocks', company, user: u });
                            }}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Manage Locks"
                          >
                            <Settings size={18} />
                          </button>
                          {isPasswordMode && (
                          <button
                            onClick={() => {
                              clearMessages();
                              setResetUserId(u.id);
                              setResetPassword('');
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Reset Password"
                          >
                            <Key size={18} />
                          </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(company.id, u)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!isLoadingUsers && companyUsers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500 text-sm">
                        {debouncedUserSearch ? 'No users match this search' : 'No users yet - add one above'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── ADD USER VIEW ──────────────────────────────────────────
  if (view.kind === 'addUser') {
    const { company } = view;
    const isPasswordMode = (company.login_mode ?? 'password') === 'password';
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => setView({ kind: 'detail', company })}
          className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to {company.name} users
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="h-1 bg-indigo-500" />
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center">
              <UserPlus className="h-5 w-5 mr-2 text-indigo-500" />
              Add User to {company.name}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {isPasswordMode ? (
                <>Login: <span className="font-mono text-slate-700">{company.code}</span> + username + password</>
              ) : (
                <>Login: <span className="font-mono text-slate-700">{company.code}</span> + access code only</>
              )}
            </p>
          </div>
          <form onSubmit={handleCreateUser} className="p-6 space-y-5">
            <FeedbackBanner />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                  {isPasswordMode ? 'Username *' : 'Access Code *'}
                </label>
                <input
                  type="text"
                  placeholder={isPasswordMode ? 'john.doe' : 'acc-001'}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm lowercase focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">3-50 chars, lowercase, letters, numbers, dots, hyphens</p>
              </div>
              {isPasswordMode && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">Password *</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Min 8 chars, 1 uppercase, 1 number"
                    className="w-full px-4 py-2.5 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">First Name</label>
                <input
                  type="text"
                  placeholder="John"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">Last Name</label>
                <input
                  type="text"
                  placeholder="Doe"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Role</label>
              <div className="flex gap-3">
                {(['user', 'admin'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setNewRole(r)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${
                      newRole === r
                        ? r === 'admin'
                          ? 'bg-violet-50 text-violet-700 border-violet-300 ring-2 ring-violet-500/20'
                          : 'bg-indigo-50 text-indigo-700 border-indigo-300 ring-2 ring-indigo-500/20'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <Shield className="h-4 w-4" />
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Admin users can create/delete other users in this company
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setView({ kind: 'detail', company })}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
              >
                {actionLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── MANAGE LOCKS VIEW ──────────────────────────────────────
  if (view.kind === 'manageLocks') {
    const { company, user: targetUser } = view;

    // IDs already assigned (to filter available locks)
    const assignedUnitIds = new Set(assignedUnits.map((a) => a.unit_id));
    const assignedGroupIds = new Set(assignedGroups.map((a) => a.group_id));

    const unassignedLocks = availableLocks.filter((l) => !assignedUnitIds.has(l.id));
    const unassignedGroups = availableGroups.filter((g) => !assignedGroupIds.has(g.id));

    // Apply filter
    const filterLower = lockAssignFilter.toLowerCase();
    const filteredAssignedUnits = assignedUnits.filter(a =>
      !lockAssignFilter || a.lock_name.toLowerCase().includes(filterLower) || a.mac_address?.toLowerCase().includes(filterLower)
    );
    const filteredAssignedGroups = assignedGroups.filter(a =>
      !lockAssignFilter || a.group_name.toLowerCase().includes(filterLower)
    );
    const filteredUnassignedLocks = unassignedLocks.filter(l =>
      !lockAssignFilter || l.name.toLowerCase().includes(filterLower) || l.mac_address?.toLowerCase().includes(filterLower)
    );
    const filteredUnassignedGroups = unassignedGroups.filter(g =>
      !lockAssignFilter || g.name.toLowerCase().includes(filterLower)
    );

    return (
      <>
        <ConfirmDialog confirmation={confirmation} />
        <div className="space-y-6">
          <button
            onClick={() => setView({ kind: 'detail', company })}
            className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to {company.name} users
          </button>

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="h-1 bg-amber-500" />
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Settings className="h-5 w-5 text-amber-500" />
                Manage Locks — {targetUser.company_username}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {targetUser.full_name || targetUser.company_username} • {company.name}
              </p>
            </div>
          </div>

          <FeedbackBanner />

          {/* Filter */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden p-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Filter locks and groups..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all shadow-sm"
                value={lockAssignFilter}
                onChange={(e) => setLockAssignFilter(e.target.value)}
              />
            </div>
          </div>

          {locksLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-indigo-600" />
            </div>
          ) : (
            <>
              {/* ══════════════════════════════════════════════════════════ */}
              {/* ══  GROUPS CARD  ═════════════════════════════════════════ */}
              {/* ══════════════════════════════════════════════════════════ */}
              {(filteredAssignedGroups.length > 0 || filteredUnassignedGroups.length > 0) && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  {/* Card header */}
                  <div className="h-1 bg-violet-500" />
                  <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-900 flex items-center">
                      <FolderOpen className="h-5 w-5 mr-2 text-violet-500" />
                      Groups
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">Assigning a group gives access to all locks in it</p>
                  </div>

                  {/* ── Assigned groups sub-section ── */}
                  {filteredAssignedGroups.length > 0 && (
                    <>
                      <div className="px-6 py-2.5 bg-emerald-50/40 border-b border-slate-100">
                        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600">
                          Assigned ({filteredAssignedGroups.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {filteredAssignedGroups.map((a) => (
                          <div key={a.assignment_id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                            <div className="flex items-center gap-3">
                              <FolderOpen className="h-4 w-4 text-violet-500" />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{a.group_name}</p>
                                <p className="text-xs text-slate-400">{a.lock_count} lock(s)</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleToggleSettings(company.id, targetUser.id, a.assignment_id, a.allow_settings_changes, 'group')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                                  a.allow_settings_changes
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                    : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                                }`}
                                title={a.allow_settings_changes ? 'Can edit settings — click to revoke' : 'Cannot edit settings — click to allow'}
                              >
                                {a.allow_settings_changes ? (
                                  <><ToggleRight className="h-3.5 w-3.5" /> Settings</>
                                ) : (
                                  <><ToggleLeft className="h-3.5 w-3.5" /> No settings</>
                                )}
                              </button>
                              <button
                                onClick={() => handleUnassignGroup(company.id, targetUser.id, a.assignment_id, a.group_name)}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove group"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* ── Available groups sub-section ── */}
                  {filteredUnassignedGroups.length > 0 && (
                    <>
                      <div className="px-6 py-2.5 bg-slate-50 border-y border-slate-100">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Available ({filteredUnassignedGroups.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {filteredUnassignedGroups.map((group) => (
                          <div key={group.id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                            <div className="flex items-center gap-3">
                              <FolderOpen className="h-4 w-4 text-slate-400" />
                              <div>
                                <p className="text-sm font-medium text-slate-700">{group.name}</p>
                                <p className="text-xs text-slate-400">{group.lock_count} lock(s)</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleAssignGroup(company.id, targetUser.id, group.id, settingsChecked[`group-${group.id}`] || false)}
                                disabled={actionLoading}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                                title="Assign group"
                              >
                                <Plus className="h-3.5 w-3.5" /> Add
                              </button>
                              <button
                                type="button"
                                onClick={() => setSettingsChecked(prev => ({ ...prev, [`group-${group.id}`]: !prev[`group-${group.id}`] }))}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs border transition-all ${
                                  settingsChecked[`group-${group.id}`]
                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                                    : 'bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-500 hover:border-slate-300'
                                }`}
                                title="Allow settings changes when assigned"
                              >
                                {settingsChecked[`group-${group.id}`]
                                  ? <ToggleRight className="h-3 w-3" />
                                  : <ToggleLeft className="h-3 w-3" />}
                                settings
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ══════════════════════════════════════════════════════════ */}
              {/* ══  LOCKS CARD  ══════════════════════════════════════════ */}
              {/* ══════════════════════════════════════════════════════════ */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Card header */}
                <div className="h-1 bg-amber-500" />
                <div className="px-6 py-4 border-b border-slate-100">
                  <h3 className="text-base font-bold text-slate-900 flex items-center">
                    <Settings className="h-5 w-5 mr-2 text-amber-500" />
                    Individual Locks
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">Manage individual lock access for this user</p>
                </div>

                {/* ── Assigned locks sub-section ── */}
                <div className="px-6 py-2.5 bg-emerald-50/40 border-b border-slate-100">
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600">
                    Assigned ({filteredAssignedUnits.length})
                  </span>
                </div>
                {filteredAssignedUnits.length === 0 ? (
                  <div className="py-6 text-center text-slate-400 text-sm border-b border-slate-100">
                    No individual locks assigned yet
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredAssignedUnits.map((a) => (
                      <div key={a.assignment_id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                        <div className="flex items-center gap-3">
                          <Settings className="h-4 w-4 text-amber-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{a.lock_name}</p>
                            <p className="text-xs text-slate-400 font-mono">{a.mac_address}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleToggleSettings(company.id, targetUser.id, a.assignment_id, a.allow_settings_changes, 'unit')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                              a.allow_settings_changes
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                            }`}
                            title={a.allow_settings_changes ? 'Can edit settings — click to revoke' : 'Cannot edit settings — click to allow'}
                          >
                            {a.allow_settings_changes ? (
                              <><ToggleRight className="h-3.5 w-3.5" /> Settings</>
                            ) : (
                              <><ToggleLeft className="h-3.5 w-3.5" /> No settings</>
                            )}
                          </button>
                          <button
                            onClick={() => handleUnassignLock(company.id, targetUser.id, a.assignment_id, a.lock_name)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove lock"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Available locks sub-section ── */}
                <div className="px-6 py-2.5 bg-slate-50 border-y border-slate-100">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Available ({filteredUnassignedLocks.length})
                  </span>
                </div>
                {filteredUnassignedLocks.length === 0 ? (
                  <div className="py-6 text-center text-slate-400 text-sm">
                    {availableLocks.length === 0
                      ? 'No locks found in your account. Add locks via the Lockwaste app first.'
                      : 'All locks are already assigned to this user.'}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredUnassignedLocks.map((lock) => (
                      <div key={lock.id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                        <div className="flex items-center gap-3">
                          <Settings className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">{lock.name}</p>
                            <p className="text-xs text-slate-400 font-mono">{lock.mac_address}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleAssignLock(company.id, targetUser.id, lock.id, settingsChecked[`lock-${lock.id}`] || false)}
                            disabled={actionLoading}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                            title="Assign lock"
                          >
                            <Plus className="h-3.5 w-3.5" /> Add
                          </button>
                          <button
                            type="button"
                            onClick={() => setSettingsChecked(prev => ({ ...prev, [`lock-${lock.id}`]: !prev[`lock-${lock.id}`] }))}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs border transition-all ${
                              settingsChecked[`lock-${lock.id}`]
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                                : 'bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-500 hover:border-slate-300'
                            }`}
                            title="Allow settings changes when assigned"
                          >
                            {settingsChecked[`lock-${lock.id}`]
                              ? <ToggleRight className="h-3 w-3" />
                              : <ToggleLeft className="h-3 w-3" />}
                            settings
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  return null;
};

export default CompanyManagement;
